import { z } from 'zod';
import { DAY_MS, startOfUtcDay, startOfUtcWeek, WEEK_MS } from './weeks.js';
/**
 * Is a weight goal on course, and what would it actually take?
 *
 * ── What this module is, and what it refuses to be ────────────────────────
 *
 * A calculator with reference ranges. Never a prescription. Everything here
 * is an estimate about a population, applied to one person, and the wording
 * that consumes it has to keep saying so. Three rules, and they are load
 * bearing rather than decorative:
 *
 *   1. Never phrase a number as an instruction. "Your goal would require
 *      roughly an 900 kcal/day deficit" is a calculation. "Eat 900 kcal
 *      less" is advice, and this app is not qualified to give it.
 *   2. Never call a rate safe or unsafe FOR THIS PERSON. It is inside or
 *      outside a commonly cited range, and that is all anyone here knows.
 *   3. Flag a figure below the floor; do not withhold it. An implied intake
 *      under `MIN_IMPLIED_INTAKE_KCAL` is reported WITH
 *      `belowIntakeFloor: true` beside it, so the caller can show the number
 *      and the warning together.
 *
 *      This used to withhold it, and the owner asked for it back. They were
 *      right, and on this project's own terms: the standing rule is that the
 *      app never decides what is better for the reader, and hiding a figure
 *      computed from somebody's own goal is exactly that decision, made
 *      about their own body. The warning is what carries the safety; the
 *      blank carried only condescension.
 *
 *      The one figure still not returned is an intake that is NEGATIVE —
 *      where the required gap exceeds total expenditure. That is withheld
 *      for a different reason and it is not safety: a negative intake does
 *      not describe anything, so there is no number to show.
 *
 * ── Both directions ───────────────────────────────────────────────────────
 *
 * Losing and gaining are the same arithmetic with a sign, and this module
 * treats them that way. The project's standing rule is that the app never
 * decides which way is better for the reader — the goal's own baseline and
 * target say which way they chose to go. So "deficit" and "surplus" are the
 * same field, `dailyEnergyGapKcal`, signed: negative is a deficit, positive
 * a surplus. A gain goal is not an afterthought here and has its own tests.
 */
// ---------------------------------------------------------------------------
// The optional profile
// ---------------------------------------------------------------------------
export const ACTIVITY_LEVELS = ['sedentary', 'light', 'moderate', 'high', 'very-high'];
/**
 * The multipliers on top of BMR. Conventional figures, and conventional is
 * the point: they are the ones the reader will find if they go looking, so a
 * number here should never disagree with the one on the other calculator
 * they checked.
 */
export const ACTIVITY_FACTORS = {
    sedentary: 1.2,
    light: 1.375,
    moderate: 1.55,
    high: 1.725,
    'very-high': 1.9,
};
/**
 * `sex` selects which constant of the Mifflin–St Jeor equation is used
 * (+5 or −161). It is a term in a formula, not a claim about the reader, and
 * the form that collects it says so. The whole profile is optional; without
 * it every profile-free figure below still works.
 */
export const bodyProfileSchema = z.object({
    sex: z.enum(['male', 'female']),
    /** Date only — a birthday has no time of day, and `z.string().datetime()`
     *  (which the rest of this codebase uses for timestamps) would demand one. */
    birthDate: z.string().date(),
    heightCm: z.number().min(80).max(260),
    activityLevel: z.enum(ACTIVITY_LEVELS),
});
export const bodyProfileInputSchema = bodyProfileSchema;
// ---------------------------------------------------------------------------
// Constants the guards are built from
// ---------------------------------------------------------------------------
/** Energy in a kilogram of body mass, the conventional first-order figure. */
export const KCAL_PER_KG = 7700;
/** The moderate range this app compares a required deficit against. */
export const MODERATE_DEFICIT_KCAL = { min: 500, max: 750 };
/**
 * The floor under any implied intake. Below roughly this, a diet is the kind
 * of thing NIDDK says needs medical supervision — so the figure is FLAGGED
 * with `belowIntakeFloor`, and shown. See rule 3 in the header for why it is
 * no longer withheld.
 */
export const MIN_IMPLIED_INTAKE_KCAL = 1200;
/**
 * Narrower than this and a band is not a band. Chosen to be far below any
 * difference a reader could act on: 10 grams a week is noise in a bathroom
 * scale, let alone in a plan.
 */
export const COLLAPSED_BAND_KG = 0.01;
/**
 * The commonly cited gradual range, as a fraction of current body weight per
 * week. A fraction rather than a flat 0.45–0.9 kg because the same absolute
 * rate is a very different thing at 55 kg and at 140 kg.
 */
export const GRADUAL_LOSS_FRACTION = { min: 0.005, max: 0.01 };
/** Gaining is conventionally slower than losing. */
export const GRADUAL_GAIN_FRACTION = { min: 0.0025, max: 0.005 };
// ---------------------------------------------------------------------------
// Internal helpers — direction arithmetic
//
// Both "loss" and "gain" (and, loosely, "neutral") reduce to one idea: how
// far is a value from the target, measured so that "closer to the target" is
// always positive. Everything signed the way the goal itself is signed
// (actualChangeKg, cumulativeKg, remainingKg, requiredRateKgPerWeek) is built
// from this pair, so a chart can lay them over one another without a caller
// having to know which direction the goal runs.
// ---------------------------------------------------------------------------
/** Positive while the target has not yet been reached; negative once it has
 *  been passed. For `neutral` there is no "side" to overshoot from, so this
 *  is the plain remaining distance and never goes negative. */
function remainingKgToward(current, target, direction) {
    switch (direction) {
        case 'higher-is-better':
            return target - current;
        case 'lower-is-better':
            return current - target;
        case 'neutral':
            return Math.abs(current - target);
    }
}
/** Positive when `from -> to` moved closer to the target, whichever way the
 *  goal runs. */
function movementTowardsTarget(from, to, target, direction) {
    return remainingKgToward(from, target, direction) - remainingKgToward(to, target, direction);
}
/** Is `targetWeightKg` reached, judging only by which side of `startWeightKg`
 *  it sits on — independent of any energy-gap sign, so a caller's inputs
 *  never have to agree with each other for this to be reliable. */
function targetSideReached(currentWeightKg, startWeightKg, targetWeightKg) {
    if (targetWeightKg < startWeightKg)
        return currentWeightKg <= targetWeightKg;
    if (targetWeightKg > startWeightKg)
        return currentWeightKg >= targetWeightKg;
    return true;
}
// ---------------------------------------------------------------------------
// Energy
// ---------------------------------------------------------------------------
export function ageYearsOn(birthDate, on) {
    const parts = birthDate.split('-');
    const birthYear = Number(parts[0]);
    const birthMonth = Number(parts[1]);
    const birthDay = Number(parts[2]);
    let age = on.getUTCFullYear() - birthYear;
    const hadBirthdayThisYear = on.getUTCMonth() + 1 > birthMonth || (on.getUTCMonth() + 1 === birthMonth && on.getUTCDate() >= birthDay);
    if (!hadBirthdayThisYear)
        age -= 1;
    return age;
}
/** Mifflin–St Jeor. */
export function basalMetabolicRate(input) {
    const { sex, ageYears, heightCm, weightKg } = input;
    const base = 10 * weightKg + 6.25 * heightCm - 5 * ageYears;
    return sex === 'male' ? base + 5 : base - 161;
}
export function totalDailyEnergyExpenditure(bmr, level) {
    return bmr * ACTIVITY_FACTORS[level];
}
/**
 * The level-1 estimate: kilograms times 7700, spread over the days left.
 * Signed — negative for a deficit, positive for a surplus.
 *
 * Deliberately labelled an estimate wherever it surfaces. It assumes a
 * metabolism that does not change as the body does, which is not how bodies
 * behave over months — but it is recomputed from the reader's CURRENT weight
 * on every measurement, which is what keeps it honest over time.
 */
export function requiredDailyEnergyGap(input) {
    const { remainingKg, days } = input;
    if (days <= 0 || remainingKg === 0)
        return null;
    return (remainingKg * KCAL_PER_KG) / days;
}
export function weeklyPlanSeries(input) {
    const { readings, goal, now } = input;
    const startDate = new Date(goal.startDate);
    if (Number.isNaN(startDate.getTime()))
        return [];
    const targetDate = goal.targetDate !== null ? new Date(goal.targetDate) : null;
    if (targetDate !== null && Number.isNaN(targetDate.getTime()))
        return [];
    /**
     * The grid runs to the DEADLINE, not to today.
     *
     * The weeks still to come are the ones the reader can do something about,
     * and showing the required rate across them is what turns the chart from a
     * report into a plan. Past the deadline (or with no deadline at all) there
     * is nothing ahead to draw, so it stops at today.
     */
    const endBound = targetDate !== null && targetDate.getTime() > now.getTime() ? targetDate : now;
    const gridStart = startOfUtcWeek(startDate);
    const gridEnd = startOfUtcWeek(endBound);
    if (gridEnd.getTime() < gridStart.getTime())
        return [];
    const sorted = readings
        .map((r) => ({ value: r.value, time: new Date(r.recordedAt).getTime() }))
        .filter((r) => !Number.isNaN(r.time))
        .sort((a, b) => a.time - b.time);
    const effectiveBaseline = goal.baselineValue ?? sorted[0]?.value ?? goal.targetValue;
    const points = [];
    let lastKnownValue = null;
    // Week-over-week level, carried across gap weeks: only updated on a week
    // that actually has readings, so the week after a gap compares against the
    // last week that had one, never against nothing.
    let lastLevel = null;
    /** The rate as of the most recent week that has happened — carried forward
     *  across every week still to come. */
    let requiredNow = null;
    for (let t = gridStart.getTime(); t <= gridEnd.getTime(); t += WEEK_MS) {
        const weekEnd = t + WEEK_MS;
        const inWeek = sorted.filter((r) => r.time >= t && r.time < weekEnd);
        if (inWeek.length > 0) {
            lastKnownValue = inWeek[inWeek.length - 1].value;
        }
        const currentValue = lastKnownValue ?? effectiveBaseline;
        // A week's level is the MEAN of that week's readings, not its last one:
        // that is what keeps a daily-weigher's bar stable instead of swinging on
        // "Monday morning vs Sunday morning", which is mostly water. The change
        // is WEEK-OVER-WEEK — this week's level against the most recent earlier
        // week that had one — never a within-week first-vs-last comparison,
        // which would leave anyone who weighs in once a week with no signal at
        // all.
        let actualChangeKg = null;
        if (inWeek.length > 0) {
            const level = inWeek.reduce((sum, r) => sum + r.value, 0) / inWeek.length;
            const previousLevel = lastLevel ?? goal.baselineValue;
            if (previousLevel !== null) {
                actualChangeKg = movementTowardsTarget(previousLevel, level, goal.targetValue, goal.direction);
            }
            lastLevel = level;
        }
        const cumulativeKg = movementTowardsTarget(effectiveBaseline, currentValue, goal.targetValue, goal.direction);
        // A week is "future" once its whole span is still ahead of now.
        const inFuture = t > now.getTime();
        /**
         * What this week had to deliver.
         *
         * For a week that HAPPENED, from the weight as it stood then and the
         * time that was left then — which is what makes the line ease off after
         * a good week and steepen after a bad one.
         *
         * For a week still AHEAD, the same figure as of NOW, held flat. That is
         * the bar to clear each week from here, and it is the only honest answer:
         * computing each future week from today's weight and a shrinking
         * deadline asks "what if I do nothing until then", which climbed from
         * 0.66 to 20.16 kg/week across the run-up and would have read as a
         * requirement that grows while the reader is on track. Meet the rate and
         * the remaining kilos fall in step with the remaining weeks, so the rate
         * stays where it is — a flat line, which is exactly what a plan is.
         */
        let requiredRateKg = null;
        if (targetDate !== null) {
            if (inFuture && requiredNow !== null) {
                requiredRateKg = requiredNow;
            }
            else {
                const daysLeftThen = (targetDate.getTime() - t) / DAY_MS;
                const remainingThen = remainingKgToward(currentValue, goal.targetValue, goal.direction);
                requiredRateKg = requiredWeeklyRate({ remainingKg: remainingThen, daysRemaining: daysLeftThen });
                if (!inFuture)
                    requiredNow = requiredRateKg;
            }
        }
        points.push({
            weekStart: new Date(t).toISOString(),
            actualChangeKg: inFuture ? null : actualChangeKg,
            requiredRateKg,
            cumulativeKg,
            inFuture,
        });
    }
    return points;
}
export function requiredWeeklyRate(input) {
    const { remainingKg, daysRemaining } = input;
    if (daysRemaining <= 0)
        return null;
    return remainingKg / (daysRemaining / 7);
}
/**
 * The band drawn on the chart: what a moderate deficit would actually
 * produce for THIS person, which is why it needs the profile. Without one
 * there is no band — the reader was told as much when they skipped it.
 *
 * Distinct from `gradualReferenceRange` below, which is the population
 * figure and needs nothing.
 */
export function healthyWeeklyRange(input) {
    const { profile, weightKg, direction, now } = input;
    if (!(weightKg > 0))
        return null;
    const ageYears = ageYearsOn(profile.birthDate, now);
    const bmr = basalMetabolicRate({ sex: profile.sex, ageYears, heightCm: profile.heightCm, weightKg });
    const tdee = totalDailyEnergyExpenditure(bmr, profile.activityLevel);
    if (!Number.isFinite(tdee) || tdee <= 0)
        return null;
    const kgPerWeekAtGap = (gapKcalPerDay) => (Math.abs(gapKcalPerDay) * 7) / KCAL_PER_KG;
    const profileMin = kgPerWeekAtGap(MODERATE_DEFICIT_KCAL.min);
    const profileMax = kgPerWeekAtGap(MODERATE_DEFICIT_KCAL.max);
    const ref = gradualReferenceRange({ currentWeightKg: weightKg, direction });
    // CLAMP the moderate-deficit-derived band into the population range, never
    // intersect it: for a gain goal the moderate 500-750kcal surplus is a
    // faster pace than the gradual-gain fraction allows at all, so an
    // intersection is empty. A profile must still produce a band whenever the
    // reference range exists, just pinned to its fast end.
    const clamp = (value) => Math.min(Math.max(value, ref.min), ref.max);
    const min = clamp(profileMin);
    const max = clamp(profileMax);
    // COLLAPSED, not merely inverted, is the case that actually happens. For a
    // gain goal the whole moderate band sits above the gradual-gain fraction,
    // so clamping pins both ends to the same edge and the "band" comes out
    // 0.310-0.310 — zero width, which draws as a hairline and tells the reader
    // nothing. When the profile's own band lands entirely outside the
    // population range, the population range IS the answer, so return it whole
    // rather than a pin at its edge.
    if (max - min < COLLAPSED_BAND_KG) {
        return { min: ref.min, max: ref.max };
    }
    return { min, max };
}
/** The commonly cited gradual range. Profile-free, so a viewer of a shared
 *  node sees it too. */
export function gradualReferenceRange(input) {
    const { currentWeightKg, direction } = input;
    const fraction = direction === 'higher-is-better' ? GRADUAL_GAIN_FRACTION : GRADUAL_LOSS_FRACTION;
    const weight = Math.abs(currentWeightKg);
    return { min: weight * fraction.min, max: weight * fraction.max };
}
// ---------------------------------------------------------------------------
// Where this is heading
// ---------------------------------------------------------------------------
/** Extrapolates the reader's own recent rate. Null when there is not enough
 *  history, or when the recent rate is going the wrong way entirely. */
export function projectedArrival(input) {
    const { recentRateKgPerWeek, remainingKg, from } = input;
    if (!Number.isFinite(remainingKg) || !Number.isFinite(recentRateKgPerWeek))
        return null;
    if (remainingKg <= 0)
        return null;
    if (recentRateKgPerWeek <= 0)
        return null;
    const weeksNeeded = remainingKg / recentRateKgPerWeek;
    if (!Number.isFinite(weeksNeeded))
        return null;
    const arrival = new Date(from.getTime() + weeksNeeded * WEEK_MS);
    if (Number.isNaN(arrival.getTime()))
        return null;
    return arrival.toISOString();
}
/**
 * The deadline that WOULD work at a gradual pace — what the reader asked
 * for: not merely "this is no longer achievable", but the date that is.
 *
 * Uses the profile's own dynamic model when there is one, and the population
 * reference range when there is not, so it can always answer. `basis` says
 * which, because a date derived from a reference range deserves different
 * wording from one derived from the reader's own numbers.
 */
export function healthyDeadline(input) {
    const { profile, currentWeightKg, targetWeightKg, direction, from } = input;
    if (profile !== null) {
        /**
         * The fastest pace that still sits inside what "gradual" means, measured
         * from the reader's weight today.
         *
         * It assumes they act on what this page tells them, which is fair
         * because the page is told: every measurement recomputes expenditure
         * from the weight they have now, and the required deficit with it.
         *
         * A second model of somebody who never revisits their intake was drawn
         * beside this one for a while. It has been removed: it warned about a
         * failure this app exists to prevent, which is a strange thing to give
         * permanent room to on the page doing the preventing.
         */
        const range = healthyWeeklyRange({ profile, weightKg: currentWeightKg, direction, now: from });
        if (range === null || !(range.max > 0))
            return null;
        const remaining = Math.abs(targetWeightKg - currentWeightKg);
        const weeks = remaining / range.max;
        if (!Number.isFinite(weeks))
            return null;
        // From the moment itself, not the midnight that opened its day. Rounding
        // down here put the sentence a day ahead of the line the chart draws from
        // the same reading — 24 July against 25 July, from one weigh-in at 08:00.
        const date = new Date(from.getTime() + weeks * WEEK_MS);
        return { date: date.toISOString(), basis: 'profile' };
    }
    const ref = gradualReferenceRange({ currentWeightKg, direction });
    if (!(ref.max > 0))
        return null;
    const remaining = Math.abs(targetWeightKg - currentWeightKg);
    const weeks = remaining / ref.max;
    if (!Number.isFinite(weeks))
        return null;
    const date = new Date(from.getTime() + weeks * WEEK_MS);
    return { date: date.toISOString(), basis: 'reference' };
}
export function assessPlan(input) {
    const { readings, goal, profile, now } = input;
    const sortedReadings = readings
        .map((r) => ({ value: r.value, time: new Date(r.recordedAt).getTime() }))
        .filter((r) => !Number.isNaN(r.time))
        .sort((a, b) => a.time - b.time);
    const currentValue = sortedReadings.length > 0 ? sortedReadings[sortedReadings.length - 1].value : null;
    const referenceWeight = currentValue ?? goal.baselineValue ?? goal.targetValue;
    const gradualRange = gradualReferenceRange({ currentWeightKg: referenceWeight, direction: goal.direction });
    const targetDate = goal.targetDate !== null ? new Date(goal.targetDate) : null;
    const daysRemaining = targetDate !== null && !Number.isNaN(targetDate.getTime()) ? (targetDate.getTime() - now.getTime()) / DAY_MS : null;
    const remainingKg = currentValue !== null ? remainingKgToward(currentValue, goal.targetValue, goal.direction) : null;
    const requiredRateKgPerWeek = remainingKg !== null && daysRemaining !== null
        ? requiredWeeklyRate({ remainingKg, daysRemaining })
        : null;
    // Recent pace, from the reader's own weekly series — a handful of recent
    // weeks so a single salty Tuesday cannot dominate.
    const series = weeklyPlanSeries({ readings, goal, now });
    const recentChanges = series
        .map((p) => p.actualChangeKg)
        .filter((v) => v !== null)
        .slice(-4);
    const recentRateKgPerWeek = recentChanges.length > 0 ? recentChanges.reduce((sum, v) => sum + v, 0) / recentChanges.length : null;
    const projectedArrivalDate = remainingKg !== null && recentRateKgPerWeek !== null
        ? projectedArrival({ recentRateKgPerWeek, remainingKg, from: now })
        : null;
    // How far ahead or behind the original straight line to the deadline.
    let aheadByKg = null;
    if (goal.baselineValue !== null && currentValue !== null && targetDate !== null && !Number.isNaN(targetDate.getTime())) {
        const startMs = new Date(goal.startDate).getTime();
        if (!Number.isNaN(startMs)) {
            const totalDays = (targetDate.getTime() - startMs) / DAY_MS;
            if (totalDays > 0) {
                const elapsedDays = Math.min(Math.max((now.getTime() - startMs) / DAY_MS, 0), totalDays);
                const originalTotalKg = remainingKgToward(goal.baselineValue, goal.targetValue, goal.direction);
                const expectedByNow = originalTotalKg * (elapsedDays / totalDays);
                const actualCumulative = movementTowardsTarget(goal.baselineValue, currentValue, goal.targetValue, goal.direction);
                aheadByKg = actualCumulative - expectedByNow;
            }
        }
    }
    const healthyRange = profile !== null
        ? healthyWeeklyRange({ profile, weightKg: referenceWeight, direction: goal.direction, now })
        : null;
    let energy = null;
    const weightForEnergy = currentValue ?? goal.baselineValue;
    if (profile !== null && weightForEnergy !== null) {
        const ageYears = ageYearsOn(profile.birthDate, now);
        const bmr = basalMetabolicRate({ sex: profile.sex, ageYears, heightCm: profile.heightCm, weightKg: weightForEnergy });
        const tdee = totalDailyEnergyExpenditure(bmr, profile.activityLevel);
        const rawRemainingKg = goal.targetValue - weightForEnergy;
        const requiredDailyGapKcal = daysRemaining !== null ? requiredDailyEnergyGap({ remainingKg: rawRemainingKg, days: daysRemaining }) : null;
        let impliedIntakeKcal = null;
        let belowIntakeFloor = false;
        if (requiredDailyGapKcal !== null) {
            const implied = tdee + requiredDailyGapKcal;
            belowIntakeFloor = implied < MIN_IMPLIED_INTAKE_KCAL;
            // Shown whenever it describes something. A negative intake does not,
            // and that is the only case left null — see rule 3.
            impliedIntakeKcal = implied > 0 ? implied : null;
        }
        energy = { bmrKcal: bmr, tdeeKcal: tdee, requiredDailyGapKcal, impliedIntakeKcal, belowIntakeFloor };
    }
    /**
     * Measured from the last reading, not from today.
     *
     * `currentWeightKg` is the weight at the last weigh-in, so counting the
     * weeks from NOW would start a journey at a point in the past and measure
     * it from the present — arriving up to a week earlier than the line the
     * chart draws from that same reading. The sentence and the line have to
     * agree, and a test holds them to it.
     */
    const lastReadingAt = sortedReadings.length > 0 ? new Date(sortedReadings[sortedReadings.length - 1].time) : now;
    const healthyDeadlineResult = currentValue !== null
        ? healthyDeadline({
            profile,
            currentWeightKg: currentValue,
            targetWeightKg: goal.targetValue,
            direction: goal.direction,
            from: lastReadingAt,
        })
        : null;
    // ---- Verdict, in the priority order the module is specified against ----
    let verdict;
    const reachedByValue = remainingKg !== null && remainingKg <= 0;
    if (reachedByValue || goal.achievedAt) {
        verdict = 'achieved';
    }
    else if (goal.targetDate === null) {
        verdict = 'open-ended';
    }
    else if (goal.baselineValue === null || currentValue === null) {
        verdict = 'unknown';
    }
    else if (requiredRateKgPerWeek === null) {
        // The deadline has already passed and the goal was not reached — there is
        // no pace, gradual or otherwise, that still gets there in time.
        verdict = 'not-gradually-reachable';
    }
    else if (requiredRateKgPerWeek <= gradualRange.min) {
        verdict = 'comfortable';
    }
    else if (requiredRateKgPerWeek <= gradualRange.max) {
        verdict = 'ambitious';
    }
    else if (profile !== null && energy !== null && energy.belowIntakeFloor) {
        verdict = 'not-gradually-reachable';
    }
    else {
        verdict = 'beyond-gradual';
    }
    /**
     * Where the fastest gradual pace lands by the EXISTING deadline. Uses the
     * same rate as `healthyDeadline`, so the two can never suggest different
     * things about the same plan.
     */
    let achievableByDeadline = null;
    if (currentValue !== null && daysRemaining !== null && daysRemaining > 0) {
        const rate = (profile !== null
            ? healthyWeeklyRange({ profile, weightKg: currentValue, direction: goal.direction, now })?.max
            : null) ??
            gradualReferenceRange({ currentWeightKg: currentValue, direction: goal.direction }).max;
        const weeks = daysRemaining / 7;
        const towards = rate * weeks;
        const distance = Math.abs(goal.targetValue - currentValue);
        const moved = Math.min(towards, distance);
        achievableByDeadline =
            goal.targetValue < currentValue ? currentValue - moved : currentValue + moved;
    }
    return {
        verdict,
        remainingKg,
        daysRemaining,
        requiredRateKgPerWeek,
        recentRateKgPerWeek,
        projectedArrival: projectedArrivalDate,
        aheadByKg,
        gradualRange,
        healthyRange,
        energy,
        healthyDeadline: healthyDeadlineResult,
        achievableByDeadline,
    };
}
/** A straight line from one point, at a signed kg/week rate, until it hits
 *  `target` — or until `latest`, whichever comes first. */
function lineTo(from, ratePerWeek, target, latest) {
    const distance = target - from.value;
    if (ratePerWeek <= 0 || distance === 0)
        return [];
    // The rate is a magnitude; the direction comes from the goal itself.
    const signed = distance > 0 ? ratePerWeek : -ratePerWeek;
    const weeks = distance / signed;
    if (!Number.isFinite(weeks) || weeks <= 0)
        return [];
    const arrival = from.at + weeks * WEEK_MS;
    const end = Math.min(arrival, latest);
    const endValue = from.value + ((end - from.at) / WEEK_MS) * signed;
    if (!Number.isFinite(endValue))
        return [];
    /**
     * One point a WEEK, not two endpoints.
     *
     * The line is straight either way — this changes nothing about its shape.
     * It changes what can be read off it: "no weekly points telling me at which
     * weight we should be by then" was the owner looking for a value at a given
     * week and finding a segment with nothing on it. A two-point line has no
     * answer for week eleven; a weekly one does, and the chart can mark it and
     * report it.
     */
    const out = [];
    for (let t = from.at; t < end; t += WEEK_MS) {
        out.push({
            at: new Date(t).toISOString(),
            value: from.value + ((t - from.at) / WEEK_MS) * signed,
        });
    }
    // The end itself, which the week grid almost never lands on exactly.
    out.push({ at: new Date(end).toISOString(), value: endValue });
    return out;
}
export function weightTrajectories(input) {
    const { readings, goal, assessment, profile, now } = input;
    const startMs = Date.parse(goal.startDate);
    if (!Number.isFinite(startMs))
        return null;
    const nowMs = now.getTime();
    /**
     * What actually happened: one mean level per week, plotted at the mean of
     * the TIMES those readings were taken.
     *
     * The time matters and used to be thrown away. Every week's point was
     * placed on the Monday that opened it, so somebody who weighs on a Friday
     * had every point drawn four days early and the whole measured line sat
     * left of where it belonged — which is exactly what the owner saw: "the
     * measurement itself seems also offset on the graph, it falls off to the
     * left."
     *
     * Averaging the timestamps alongside the values keeps the point where the
     * weighing happened. A week weighed once lands on that day; a week weighed
     * daily lands mid-week, which is what its mean describes.
     */
    const buckets = new Map();
    for (const reading of readings) {
        const t = Date.parse(reading.recordedAt);
        if (!Number.isFinite(t) || t < startMs || t > nowMs)
            continue;
        const week = startOfUtcWeek(new Date(t)).getTime();
        const bucket = buckets.get(week) ?? { sum: 0, timeSum: 0, count: 0 };
        bucket.sum += reading.value;
        bucket.timeSum += t;
        bucket.count += 1;
        buckets.set(week, bucket);
    }
    const actual = [...buckets.entries()]
        .sort((a, b) => a[0] - b[0])
        .map(([, bucket]) => ({
        at: new Date(Math.round(bucket.timeSum / bucket.count)).toISOString(),
        value: bucket.sum / bucket.count,
    }));
    const current = actual[actual.length - 1]?.value ?? goal.baselineValue;
    if (current === null || current === undefined)
        return null;
    // The fastest gradual pace, from the profile when there is one and from
    // the population range when there is not — the same fallback
    // `healthyDeadline` uses, so the chart and the sentence cannot disagree.
    const healthyRate = (profile !== null
        ? healthyWeeklyRange({ profile, weightKg: current, direction: goal.direction, now })?.max
        : null) ?? gradualReferenceRange({ currentWeightKg: current, direction: goal.direction }).max;
    // How far right the chart has to reach: past the deadline when a gradual
    // pace would not make it, because that gap is the whole point.
    const deadlineMs = goal.targetDate ? Date.parse(goal.targetDate) : null;
    const healthyArrival = healthyRate > 0 ? nowMs + (Math.abs(goal.targetValue - current) / healthyRate) * WEEK_MS : nowMs;
    const projectedArrivalMs = assessment.projectedArrival
        ? Date.parse(assessment.projectedArrival)
        : null;
    /**
     * The chart stops at the DEADLINE.
     *
     * It used to run on to whichever line arrived last, so a healthy pace that
     * needed another seven weeks was drawn arriving. The owner asked for the
     * goal's own span instead, and it turns out to say the same thing more
     * plainly: a line that has not reached the target line by the right-hand
     * edge has visibly not made it, and no date arithmetic is needed to see
     * it. Lines are truncated there rather than left to draw outside the plot.
     */
    const latest = deadlineMs !== null && Number.isFinite(deadlineMs) && deadlineMs > nowMs
        ? deadlineMs
        : Math.max(nowMs, Number.isFinite(healthyArrival) ? healthyArrival : nowMs, projectedArrivalMs && Number.isFinite(projectedArrivalMs) ? projectedArrivalMs : nowMs);
    /**
     * Where every forward line begins: the last measurement, in time AND in
     * value.
     *
     * It used to take the last measured VALUE but place it at `now`, which was
     * invisible while the measured points sat on their week's Monday and
     * obvious the moment they moved onto the days they were actually taken:
     * the forward lines started days to the right of where the measured line
     * stopped, hanging in the air.
     *
     * All three start here, not just the plan. Two lines leaving the last
     * reading and a third leaving today would read as a bug, and it would be
     * one.
     */
    const lastMeasuredPoint = actual[actual.length - 1];
    const fromToday = {
        at: lastMeasuredPoint ? Date.parse(lastMeasuredPoint.at) : nowMs,
        value: current,
    };
    const projected = assessment.recentRateKgPerWeek !== null && assessment.recentRateKgPerWeek > 0
        ? lineTo(fromToday, assessment.recentRateKgPerWeek, goal.targetValue, latest)
        : [];
    const baseline = goal.baselineValue;
    /** The plan as set: baseline at the start, target at the deadline, one
     *  point a week so a reader can ask what any given week was meant to be. */
    /**
     * What the weight has to do FROM HERE to arrive on the deadline.
     *
     * It begins at the last measurement, not at the goal's baseline. It used
     * to be the plan as originally set — a fixed run from the baseline on the
     * start date — which never moved however the weeks went, and left the
     * reader reading a line about a version of their goal that had stopped
     * being true. "The planned should always adjust to the latest measures.
     * It needs to continue where the last measurement stops."
     *
     * Which is also what makes the styling honest: the measured line is drawn
     * solid and this one dotted in the same accent, so they read as one line.
     * They now genuinely are — this one starts at the other one's last point.
     *
     * Falls back to the baseline on the start date when there is nothing
     * measured yet, because then that IS where the plan starts from.
     */
    const required = [];
    const fromAt = lastMeasuredPoint ? fromToday.at : startMs;
    const fromValue = lastMeasuredPoint ? lastMeasuredPoint.value : baseline;
    if (fromValue !== null && deadlineMs !== null && Number.isFinite(deadlineMs) && deadlineMs > fromAt) {
        const span = deadlineMs - fromAt;
        for (let t = fromAt; t <= deadlineMs; t += WEEK_MS) {
            const fraction = (t - fromAt) / span;
            required.push({
                at: new Date(t).toISOString(),
                value: fromValue + (goal.targetValue - fromValue) * fraction,
            });
        }
        // The deadline itself, whatever the week grid landed on.
        required.push({ at: new Date(deadlineMs).toISOString(), value: goal.targetValue });
    }
    return {
        actual,
        projected,
        required,
        healthyFromToday: lineTo(fromToday, healthyRate, goal.targetValue, latest),
        targetValue: goal.targetValue,
        deadline: goal.targetDate,
        domain: { from: new Date(startMs).toISOString(), to: new Date(latest).toISOString() },
    };
}
//# sourceMappingURL=weightPlan.js.map